// Vault crypto primitives. PBKDF2-SHA256 → AES-GCM-256.
// Keep this file small and boring. See PLAN.md Phase 1.

const subtle = globalThis.crypto?.subtle;
if (!subtle) {
  throw new Error('WebCrypto SubtleCrypto is not available in this environment');
}

export const CURRENT_VERSION = 1;
export const KDF_NAME = 'PBKDF2-SHA256';
export const DEFAULT_ITERATIONS = 600_000;
export const SALT_BYTES = 16;
export const NONCE_BYTES = 12;
export const KEY_BITS = 256;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function randomBytes(n) {
  const out = new Uint8Array(n);
  globalThis.crypto.getRandomValues(out);
  return out;
}

export function generateSalt() {
  return randomBytes(SALT_BYTES);
}

function generateNonce() {
  return randomBytes(NONCE_BYTES);
}

export async function deriveKey(masterPassword, salt, iterations = DEFAULT_ITERATIONS) {
  if (typeof masterPassword !== 'string' || masterPassword.length === 0) {
    throw new Error('masterPassword must be a non-empty string');
  }
  if (!(salt instanceof Uint8Array) || salt.byteLength !== SALT_BYTES) {
    throw new Error(`salt must be a Uint8Array of ${SALT_BYTES} bytes`);
  }
  if (!Number.isInteger(iterations) || iterations < 1) {
    throw new Error('iterations must be a positive integer');
  }

  const baseKey = await subtle.importKey(
    'raw',
    textEncoder.encode(masterPassword),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  return subtle.deriveKey(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt,
      iterations,
    },
    baseKey,
    { name: 'AES-GCM', length: KEY_BITS },
    // Not extractable: prevents the raw key bytes from being exported by JS.
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptVault(vaultData, key, options = {}) {
  if (vaultData === undefined) {
    throw new Error('vaultData is required');
  }
  if (!(key instanceof CryptoKey)) {
    throw new Error('key must be a CryptoKey');
  }
  const salt = options.salt ?? null;
  const iterations = options.iterations ?? DEFAULT_ITERATIONS;
  const nonce = generateNonce();

  const plaintext = textEncoder.encode(JSON.stringify(vaultData));
  const ctBuf = await subtle.encrypt(
    { name: 'AES-GCM', iv: nonce },
    key,
    plaintext,
  );

  return {
    version: CURRENT_VERSION,
    kdf: KDF_NAME,
    iterations,
    salt: salt ? new Uint8Array(salt) : null,
    nonce,
    ciphertext: new Uint8Array(ctBuf),
  };
}

export async function decryptVault(blob, key) {
  if (!blob || typeof blob !== 'object') {
    throw new Error('blob must be an EncryptedBlob');
  }
  if (blob.version !== CURRENT_VERSION) {
    throw new Error(`unsupported blob version: ${blob.version}`);
  }
  if (!(blob.nonce instanceof Uint8Array) || blob.nonce.byteLength !== NONCE_BYTES) {
    throw new Error('blob.nonce missing or wrong length');
  }
  if (!(blob.ciphertext instanceof Uint8Array)) {
    throw new Error('blob.ciphertext missing');
  }
  if (!(key instanceof CryptoKey)) {
    throw new Error('key must be a CryptoKey');
  }

  let plainBuf;
  try {
    plainBuf = await subtle.decrypt(
      { name: 'AES-GCM', iv: blob.nonce },
      key,
      blob.ciphertext,
    );
  } catch (err) {
    // GCM auth failure: wrong key, tampered ciphertext, or tampered nonce.
    throw new DecryptionError('decryption failed (wrong password or tampered data)', { cause: err });
  }

  const json = textDecoder.decode(plainBuf);
  try {
    return JSON.parse(json);
  } catch (err) {
    throw new DecryptionError('decrypted payload is not valid JSON', { cause: err });
  }
}

// Raw-bytes API — used for attachment payloads where JSON serialization
// would be a waste. Same AES-GCM construction, fresh nonce per call.
export async function encryptBytes(bytes, key) {
  if (!(bytes instanceof Uint8Array)) throw new Error('encryptBytes: expected Uint8Array');
  if (!(key instanceof CryptoKey)) throw new Error('encryptBytes: key must be a CryptoKey');
  const nonce = randomBytes(NONCE_BYTES);
  const ct = await subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, bytes);
  return { version: CURRENT_VERSION, nonce, ciphertext: new Uint8Array(ct) };
}

export async function decryptBytes(blob, key) {
  if (!blob || blob.version !== CURRENT_VERSION) throw new Error('decryptBytes: unsupported blob');
  if (!(blob.nonce instanceof Uint8Array) || blob.nonce.byteLength !== NONCE_BYTES) {
    throw new Error('decryptBytes: bad nonce');
  }
  if (!(key instanceof CryptoKey)) throw new Error('decryptBytes: key must be a CryptoKey');
  try {
    const plain = await subtle.decrypt({ name: 'AES-GCM', iv: blob.nonce }, key, blob.ciphertext);
    return new Uint8Array(plain);
  } catch (err) {
    throw new DecryptionError('attachment decryption failed', { cause: err });
  }
}

export class DecryptionError extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = 'DecryptionError';
  }
}

// Best-effort: WebCrypto CryptoKey is opaque; raw bytes never live in JS-visible
// memory (we set extractable=false in deriveKey). Dropping references is all we
// can do — the GC and the platform handle the rest. Callers should also drop
// their own references after calling this.
export function wipeKey(_key) {
  // Intentional no-op for the CryptoKey itself. Kept as an API surface so the
  // caller has a single, named place to express "I'm done with this key".
  return;
}
